import React, { useEffect, useState } from 'react';

import { DeleteRounded, LinkRounded } from '@mui/icons-material';
import { Stack } from '@mui/material';
import { useNavigate } from 'react-router';

import Breadcrumbs from '../../../components/Breadcrumbs';
import Button from '../../../components/Button';
import CommitForm from '../../../components/CommitForm';
import ContentGroupsList from '../../../components/ContentGroupsList';
import ContentWrapper from '../../../components/ContentWrapper';
import CopyButton from '../../../components/CopyButton';
import DetailsModal, { Values } from '../../../components/DetailsModal';
import Modal from '../../../components/Modal';
import SkeletonStack from '../../../components/SkeletonStack';
import {
  useDeleteVideoTopicMutation,
  useGetVideoCategoryQuery, useNewVideoMutation, useUpdateVideoTopicMutation,
} from '../../../services/videos';
import {
  Resource, Topic, Video,
} from '../../../services/videos/types';
import { SUBJECTS } from '../../../utils/common/constants';
import useError from '../../../utils/react/hooks/useError';
import useSuccess from '../../../utils/react/hooks/useSuccess';
import {
  BreadcrumbsContainer,
} from '../index.styles';
import ShareButton from '../ShareButton';

const VIDEO_FORM = {
  title: '',
  host: 'Cognito Tuition',
  youtubeId: '',
};

const INITIAL_RESOURCE_FORM = {
  label: '',
  href: '',
};

interface TopicPageProps {
  subject: string,
  category: string,
  topic: string
}

const TopicPage = ({ subject, category, topic }: TopicPageProps) => {
  const navigate = useNavigate();

  const { data: categoryData, isLoading } = useGetVideoCategoryQuery(
    { subject, category }, { skip: !subject || !category },
  );
  const [newResourceFormOpen, setNewResourceFormOpen] = useState<boolean>(false);
  const [newResourceForm, setNewResourceForm] = useState(INITIAL_RESOURCE_FORM);
  const [newVideoFormOpen, setNewVideoFormOpen] = useState<boolean>(false);
  const [newVideoForm, setNewVideoForm] = useState(VIDEO_FORM);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  const [newVideo, {
    isSuccess: isNewVideoSuccess,
    isLoading: isNewVideoLoading,
    error: newVideoError,
    reset: resetNewVideo,
  }] = useNewVideoMutation();
  const [updateTopic, {
    isSuccess: isUpdateTopicSuccess,
    isLoading: isUpdateTopicLoading,
    isError: isUpdateTopicError,
    error: updateTopicError,
    reset: resetUpdateTopic,
  }] = useUpdateVideoTopicMutation();
  const [removeTopicResource, {
    isSuccess: isRemoveTopicResourceSuccess,
    isLoading: isRemoveTopicResourceLoading,
    error: removeTopicResourceError,
    reset: resetRemoveTopicResource,
  }] = useUpdateVideoTopicMutation();
  // This is the same useUpdateVideoMutation as above, but is separated out
  // to avoid errors and success checks from interfering with each other
  const [deleteTopic, {
    isSuccess: isDeleteTopicSuccess,
    isError: isDeleteTopicError,
    isLoading: isDeleteTopicLoading,
    error: deleteTopicError,
  }] = useDeleteVideoTopicMutation();

  const resetModal = async () => {
    await resetNewVideo();
    setNewVideoForm(VIDEO_FORM);
    await resetUpdateTopic();
    setNewResourceForm(INITIAL_RESOURCE_FORM);
    await resetRemoveTopicResource();
  };

  const onVideoEntryCreate = (values?: Values) => {
    newVideo({
      subject,
      category,
      attributes: {
        ...values, subject, topic: `${subject}#${category}#${topic}`,
      },
    });
  };

  useEffect(() => {
    if (isDeleteTopicSuccess) {
      // Navigate up a level after a topic is deleted
      navigate(`/admin/videos/${subject}/${category}`);
    }
  }, [isDeleteTopicSuccess]);

  // Close modal after video is created
  useEffect(() => {
    if (isNewVideoSuccess) {
      setNewVideoFormOpen(false);
      resetModal();
    }
  }, [isNewVideoSuccess]);

  // Close modal after resource is added
  useEffect(() => {
    if (isUpdateTopicSuccess) {
      setNewResourceFormOpen(false);
      resetModal();
    }
  }, [isUpdateTopicSuccess]);

  useSuccess(isDeleteTopicError, 'Topic deleted');
  useError(isUpdateTopicError, updateTopicError);
  useError(isDeleteTopicError, deleteTopicError);

  const topicObj: Topic | undefined = categoryData?.data.topics.find((t: Topic) => t.key === topic);

  return (
    <div>
      {!isLoading && categoryData?.data && topicObj ? (
        <>
          <BreadcrumbsContainer>
            <Breadcrumbs
              title={topicObj.shortLabel}
              path={[
                {
                  label: SUBJECTS[subject]?.label || 'Videos',
                  href: `/admin/videos/${subject}`,
                },
                {
                  label: categoryData?.data.shortLabel,
                  href: `/admin/videos/${subject}/${category}`,
                },
              ]}
            />
          </BreadcrumbsContainer>
          <ContentWrapper title="Topic Details">
            <CommitForm
              data={topicObj}
              metadata={{
                key: {
                  editable: false,
                  label: 'Key',
                  type: 'text',
                },
                label: {
                  editable: true,
                  label: 'Label',
                  type: 'text',
                },
                shortLabel: {
                  editable: true,
                  label: 'Short Label',
                  type: 'text',
                },
              }}
              fields={{
                key: {
                  xs: 12,
                  md: 12,
                },
                label: {
                  xs: 12,
                  md: 12,
                },
                shortLabel: {
                  xs: 12,
                  md: 12,
                },
              }}
              onSubmit={async (values) => updateTopic({
                subject,
                category,
                topic,
                attributes: values,
              })}
              isSubmitLoading={isUpdateTopicLoading}
              autoSubmit
            />
          </ContentWrapper>
          <ContentWrapper title="Actions">
            <Stack direction="row" spacing={1}>
              <Button
                color="error"
                size="medium"
                onClick={() => setDeleteModalOpen(true)}
                disabled={topicObj.videos.length > 0}
                startIcon={(<DeleteRounded />)}
              >Delete
              </Button>
              <ShareButton
                targetTitle={topicObj.shortLabel}
                subject={subject}
                category={category}
                topic={topic}
              />
              <CopyButton
                size="medium"
                copyText={`${window.location.origin}/videos?subject=${subject}&category=${category}&topic=${topic}`}
                startIcon={(<LinkRounded />)}
                buttonLabel="Copy Link"
              />
            </Stack>
          </ContentWrapper>
          <ContentWrapper title="Videos">
            <ContentGroupsList
              columns={[
                {
                  field: 'title',
                  headerName: 'Title',
                  width: 360,
                },
              ]}
              data={(topicObj.videos || []).map((v) => ({
                ...v, id: v.videoId, title: v.title,
              }))}
              loading={isLoading}
              actions={{
                create: {
                  fn: () => setNewVideoFormOpen(true),
                  label: 'Add Video',
                },
                reorder: {
                  fn: async (fields) => updateTopic({
                    subject,
                    category,
                    topic,
                    attributes: {
                      videos: fields?.orderedData.map((x: Video) => x.videoId),
                    },
                  }),
                  label: 'Reorder',
                  isSuccess: isUpdateTopicSuccess,
                  error: updateTopicError,
                },
              }}
              getRowHref={(row: Video) => `/admin/video/${row.subject}/${row.videoId}`}
            />
          </ContentWrapper>
          <ContentWrapper
            title="Topic Resources"
            caption="These resources will be displayed on all videos within this topic."
          >
            <ContentGroupsList
              columns={[
                {
                  field: 'label',
                  headerName: 'Label',
                  width: 200,
                },
                {
                  field: 'href',
                  headerName: 'Link',
                  width: 300,
                },
              ]}
              actions={{
                delete: {
                  fn: async (params) => removeTopicResource({
                    subject,
                    category,
                    topic,
                    attributes: {
                      resources: params?.data
                        .filter((x: Resource) => !params?.selectedRows?.includes(x.label)),
                    },
                  }),
                  label: 'Remove',
                  isSuccess: isRemoveTopicResourceSuccess,
                  isLoading: isRemoveTopicResourceLoading,
                  error: removeTopicResourceError,
                  color: 'error',
                },
                reorder: {
                  fn: (params) => updateTopic({
                    subject,
                    category,
                    topic,
                    attributes: {
                      resources: params?.orderedData,
                    },
                  }),
                  label: 'Reorder',
                  isSuccess: isUpdateTopicSuccess,
                  isLoading: isUpdateTopicLoading,
                  error: updateTopicError,
                },
                create: {
                  fn: () => setNewResourceFormOpen(true),
                  label: 'Add Resource',
                },
              }}
              data={(topicObj.resources || []).map((x: Resource) => ({ ...x, id: x.label }))}
              loading={isLoading}
              checkboxSelection
              getRowHref={(row: Resource) => row.href}
              rowHrefTarget="_blank"
            />
          </ContentWrapper>
          <DetailsModal
            open={newVideoFormOpen}
            handleClose={() => setNewVideoFormOpen(false)}
            error={newVideoError}
            title="Add New Video"
            fields={{
              title: {
                editable: true,
                type: 'text',
              },
              host: {
                editable: true,
                type: 'text',
              },
              youtubeId: {
                editable: true,
                type: 'text',
                label: 'YouTube ID',
              },
            }}
            values={newVideoForm}
            buttons={[{
              fn: onVideoEntryCreate,
              label: 'Add',
              loading: isNewVideoLoading,
            }]}
          />
          <DetailsModal
            open={newResourceFormOpen}
            handleClose={() => setNewResourceFormOpen(false)}
            error={updateTopicError}
            title="Add New Resource"
            fields={{
              label: {
                editable: true,
                type: 'text',
                label: 'Unique Label',
              },
              href: {
                editable: true,
                type: 'text',
                label: 'Link',
              },
            }}
            values={newResourceForm}
            buttons={[{
              fn: (fields) => updateTopic({
                subject,
                category,
                topic,
                attributes: {
                  resources: [...(topicObj?.resources || []), fields],
                },
              }),
              label: 'Add',
              loading: isUpdateTopicLoading,
            }]}
          />
          <Modal
            open={deleteModalOpen}
            handleClose={() => setDeleteModalOpen(false)}
            title="Are you sure?"
            description="This will permanently delete this topic."
            buttons={[
              {
                label: 'Delete',
                onClick: () => deleteTopic({
                  subject, category, topic,
                }),
                color: 'error',
                loading: isDeleteTopicLoading,
              },
            ]}
          />
        </>
      ) : (
        <SkeletonStack />
      )}
    </div>
  );
};

export default TopicPage;
